import React, { useRef, useEffect, useState, useCallback, useContext  } from "react";
import InputBase from '@mui/material/InputBase';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';

import { useMediaQuery } from '@mui/material';
import theme from '../theme'; // ← importa tu theme personalizado

import {
    WELCOME_MESSAGE,
    MAX_LENGTH_INPUT_SEARCH
} from '../env';

// Conversation API (REST, whisperbi-back-security)
import { createConversation, getConversation, appendConversationTurn, updateConversationTurn } from '../api/ConversationApi';
//import ConversationStorage from '../utils/ConversationStorage.js';

import OptimizedChatComponent from './OptimizedChatComponent.js'; // Componente optimizado para el chat
import { DataContext } from '../context/DataContext';

// Invocación del agente y de la generación de gráficas: ambas pasan por
// Lambdas de whisperbi-agent-management, nunca por AWS SDK directo en el navegador.
import { invokeAgent } from '../api/InvokeAgentAPI';
import DashboardsService from '../services/DashboardsService';
import { initialDeepProgress, reduceDeepProgress } from './deep-analysis/deepProgress';
import { initialDashboardProgress, reduceDashboardProgress } from './dashboard/dashboardProgress';
import ChartDataAPI, { chartErrorMessage } from '../api/ChartDataAPI';
import { awaitTurnChart, recoverPendingTurn } from '../api/InvokeAgentAPI';
import { loadChatModel, saveChatModel } from '../utils/chatModelPreference';
import { unansweredTurnToRecover } from '../utils/unansweredTurn';
import { questionErrorTurn } from '../utils/questionError';
import VerifiedQueryService from '../services/VerifiedQueryService';

// Paginación real del historial (control de costos/rendimiento, 2026-09-24):
// el backend (`GET /conversation/{id}`) ya no devuelve la conversación
// completa en una sola invocación -- trae páginas de a HISTORY_PAGE_SIZE
// turnos (últimos primero), con un cursor para pedir los siguientes más
// antiguos ("cargar historial anterior"). Reemplaza a la ventana de
// renderizado puramente local que existía antes acá.
const HISTORY_PAGE_SIZE = 8; // últimas 4 preguntas + 4 respuestas por página

// Campos internos que Chat.js le agrega a cada turno en `answers` (nunca se
// mandan al backend, nunca los lee el resto de la UI):
//  - `_key`: identidad estable para el `key` de React -- ver
//    OptimizedChatComponent.js::answersListMemoized (evita que "cargar más
//    historial" remonte y re-tipee turnos ya mostrados al desplazar sus
//    índices en el array).
//  - `_serverTurnIndex`: el `turn_index` que devolvió el backend al crear
//    este turno (appendConversationTurn) -- lo necesita cualquier
//    actualización posterior (gráfica, feedback) para saber a qué item de
//    ConversationTurnsTable1 escribir.
//  - `_appendPromise`: promesa en vuelo de la creación del turno, para que
//    una actualización que llega ANTES de que el POST responda pueda
//    esperarla en vez de perderse.
//  - `_dashboardSaving` / `_dashboardSaveError`: estado de "Guardar tablero"
//    mientras se guarda (el id guardado, `savedDashboardId`, sí se persiste).
//  - `chartError`: por qué no se generó la gráfica en ESTA sesión (es
//    transitorio: al recargar el historial no debe quedar el aviso).
//  - `chartLoading`: la gráfica todavía se está generando en el servidor.
//  - `chartPending` / `invocationId`: la gráfica llega por WebSocket
//    (awaitTurnChart) en vez de pedirse a POST /agent/chart-data.
const TURN_INTERNAL_FIELDS = [
    '_key', '_serverTurnIndex', '_appendPromise', '_dashboardSaving', '_dashboardSaveError', 'chartError',
    'chartLoading', 'chartPending', 'invocationId'
];
const stripInternalTurnFields = (turnObj) => {
    const clean = { ...turnObj };
    TURN_INTERNAL_FIELDS.forEach((field) => delete clean[field]);
    return clean;
};

/** Datos de un `completed` del agente -> los campos que usa el chat. */
const answerFromAgentData = (data, queryUuid) => ({
    completion: data.completion,
    usage: data.usage,
    totalInputTokens: data.usage?.input_tokens,
    totalOutputTokens: data.usage?.output_tokens,
    // queryUuid es el id generado al enviar la pregunta y mandado a Bedrock
    // como promptSessionAttributes.queryUuid -- la clave con la que el
    // executor del agente persiste sus resultados de consulta (no
    // invocation_id, que es otro id, solo de auditoría).
    queryUuid,
    deepReport: data.deep_report,
    dashboard: data.dashboard,
    modelId: data.model_id,
    invocationId: data.invocation_id,
    chartPending: Boolean(data.chart_pending),
    creditsConsumed: data.credits_consumed
});

/** Turno de respuesta que se agrega a `answers`. */
const buildAnswerTurn = ({ completion, usage, totalInputTokens, totalOutputTokens, queryUuid, deepReport, dashboard, modelId, invocationId, chartPending, creditsConsumed }) => ({
    // Ver el `_key` de la pregunta: sin esto, al llegar la
    // gráfica el turno se remontaba y el texto se re-tipeaba.
    _key: `local-${uuidv4()}`,
    text: completion,
    usage,
    totalInputTokens,
    totalOutputTokens,
    queryUuid,
    invocationId,
    // La gráfica la genera el servidor y llega por WebSocket.
    ...(chartPending ? { chartPending: true, chartLoading: true } : {}),
    // Modelo que respondió (el elegido en el chat o el del agente).
    ...(modelId ? { modelId } : {}),
    // Créditos que cobró el turno; los de la gráfica llegan después
    // (chartCredits). Se muestran sumados al pie de la respuesta.
    ...(typeof creditsConsumed === 'number' ? { creditsConsumed } : {}),
    // Informe estructurado del Análisis Profundo; `text` lleva su
    // versión en texto plano (copiar, historial, gráfica).
    ...(deepReport ? { deepReport } : {}),
    // Tablero del modo Dashboard (consultas, definición y resultado
    // de cada mosaico); `text` lleva su resumen.
    ...(dashboard ? { dashboard } : {})
});

const Chat = ({ sessionIdChange, onSessionIdChange, onRequestNewSessionId, onSendQuery, onSendTextQuery,userName }) => {

    const WELCOME_MESSAGE = "¡Hola! ¿En qué puedo ayudarte hoy?";
    const MAX_LENGTH_INPUT_SEARCH = 500;

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    /*** Control de las respuestas *****************************/
    const [controlAnswers, setControlAnswers] = React.useState([]);
    const [answers, setAnswers] = React.useState([]);
    // Índice a partir del cual un turno es "nuevo" (generado en esta sesión
    // de navegación) en vez de historial ya cargado desde el backend --
    // decide el efecto de escritura (solo turnos nuevos, ver
    // OptimizedChatComponent.js::AnswerItem `isLive`) y a partir de dónde
    // hay que crear (POST) en vez de asumir ya guardado. Se recalcula cada
    // vez que se carga/cambia de conversación, y sube cada vez que se
    // antepone una página de historial más vieja ("cargar más").
    const [historyBoundaryIndex, setHistoryBoundaryIndex] = React.useState(0);
    // Cursor opaco que devolvió el último GET de historial -- null cuando
    // no hay más páginas antiguas que cargar (o todavía no se cargó nada).
    const [nextHistoryCursor, setNextHistoryCursor] = React.useState(null);
    const [loadingMoreHistory, setLoadingMoreHistory] = React.useState(false);
    // Espejo síncrono de answers/controlAnswers -- lo necesitan los
    // callbacks que persisten actualizaciones tardías (gráfica, feedback)
    // para resolver en qué posición ACTUAL del array está el turno que
    // están guardando, sin depender de un `index` capturado en un closure
    // viejo (que dejaría de ser válido después de "cargar más historial").
    const answersRef = useRef([]);
    const controlAnswersRef = useRef([]);
    useEffect(() => { answersRef.current = answers; }, [answers]);
    useEffect(() => { controlAnswersRef.current = controlAnswers; }, [controlAnswers]);
    // Para callbacks asíncronos (pregunta aceptada, respuesta recuperada) que
    // terminan después de un re-render: sesión y persistTurnChange vigentes.
    const sessionIdRef = useRef(null);
    // Recuperación en curso de una respuesta pendiente: { sessionId, controller }.
    // Pertenece a esa conversación; al cambiar o borrarla se cancela.
    const recoveryRef = useRef(null);
    const persistTurnChangeRef = useRef(null);
    //*********************************************************/

    const [enabled, setEnabled] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    // Etiqueta de progreso del agente ("Consultando la base de datos…"),
    // solo llega por WebSocket -- ver api/AgentSocket.js.
    const [progressLabel, setProgressLabel] = React.useState("");
    // Modo elegido en los botones del chat ("basic" | "deep" | "dashboard")
    // y progreso del Análisis Profundo en curso (deep-analysis/deepProgress.js).
    const [analysisMode, setAnalysisMode] = React.useState('basic');
    // Modelo elegido en el selector del chat; null = el del agente. Se
    // recuerda por conversación (utils/chatModelPreference).
    const [selectedModelId, setSelectedModelId] = React.useState(null);
    const [deepProgress, setDeepProgress] = React.useState(null);
    const [dashboardProgress, setDashboardProgress] = React.useState(null);
    // Cada incremento le pide a OptimizedChatComponent abrir el modal de
    // planes (respuesta plan_required del backend).
    const [plansModalRequest, setPlansModalRequest] = React.useState(0);
    const [query, setQuery] = React.useState("");

    // Se usa para enviar a layout una bandera cuando se crea el primer mensaje en el chat.
    const [firstQuery, setFirstQuery] = React.useState("false");

    /*** Workspace-related state ********************************/
    // selectedAgentId: agente elegido en el sidebar para el workspace actual
    // (AgentSelector.jsx). agent-runtime lo usa para resolver la config; si
    // es null cae al agente ACTIVE más reciente del workspace.
    const { selectedWorkspace, selectedAgentId } = useContext(DataContext);
    const [workspaceError, setWorkspaceError] = React.useState(null);
    /*********************************************************/


    /*** Manejo de la sesión ********************************/
    const [sessionId, setSessionId] = React.useState(() => sessionIdChange || null);
    // Función que LayoutApp.js puede llamar para generar un nuevo sessionId
    const generateNewSessionId = () => {
        console.log('***Se crea un nuevo sessionId');
        const newId = uuidv4();
        setSessionId(newId);
        console.log('Nuevo Session ID generado en Chat:', newId);

        // Notificar al componente padre sobre el nuevo sessionId
        if (onSessionIdChange) {
            onSessionIdChange(newId);
        }

        console.log('*** Se borran las respuestas anteriores');
        setControlAnswers([]);
        setAnswers([]);
        // Sin este reset, un chat nuevo hereda el `historyBoundaryIndex` de
        // la conversación anterior (p.ej. 40) -- el primer turno de esta
        // sesión (índice 0) quedaría marcado como "historial" (isLive=false,
        // sin efecto de escritura) en vez de nuevo.
        setHistoryBoundaryIndex(0);
        setNextHistoryCursor(null);
    };
    /*********************************************************/

    // Al cambiar de conversación, recuperar el modelo que se eligió en ella
    // (una conversación nueva arranca con el del agente).
    useEffect(() => {
        setSelectedModelId(loadChatModel(sessionId));
    }, [sessionId]);

    const handleModelChange = useCallback((modelId) => {
        setSelectedModelId(modelId);
        saveChatModel(sessionId, modelId);
    }, [sessionId]);

    const ensureActiveSessionId = useCallback(() => {
        if (sessionId) {
            return sessionId;
        }

        const newId = uuidv4();
        setSessionId(newId);
        return newId;
    }, [sessionId]);


    /**************************************************************
     * Manejo de Menus en LayaoutApp.js
     * 
     **************************************************************/
    const [hasInteractedWithInput, setHasInteractedWithInput] = useState(false);
    const [newChatCounter, setNewChatCounter] = useState(0); // Mantén este estado en LayoutApp si lo estás usando allí


    // Función para crear un nuevo chat (se llama desde LayoutApp)
    const createNewChatWithSessionId = async (sessionId) => {
        const newChatName = `Nueva conversación`;

        // Actualizar el estado local, colocando el nuevo chat al principio
        // Asumo que 'setChatList' y 'chatList' están definidos en el componente padre (LayoutApp)
        onRequestNewSessionId(prevChatList => [{ session_id: sessionId, name: newChatName }, ...prevChatList]);
        // setNewChatCounter(prevCounter => prevCounter + 1); // Esto debería estar en LayoutApp

        console.log('chatList actualizado con nuevo elemento al principio:', sessionId);
        console.log('*** newChatName: ', newChatName);

        // Crear el registro de la conversación vía el servicio web
        try {
            const apiResult = await createConversation(sessionId, newChatName, userName);
            if (apiResult.success) {
                console.log('Registro creado exitosamente:', apiResult.data.itemId);
            } else {
                console.error('Error al crear el registro de la conversación:', apiResult.error);
                // Si falla la creación, podrías informar al usuario o revertir la acción local si es necesario
            }
        } catch (error) {
            console.error('Error al crear conversación:', error);
        }
    };

    // Manejo del focus en el InputBase
    const handleInputFocus = () => {
        if (!hasInteractedWithInput) {
            const newSessionId = uuidv4();
            createNewChatWithSessionId(newSessionId);
            setHasInteractedWithInput(true);
        }
    };

    useEffect(() => {
        //if (sessionId && onSessionIdChange) {
        if (sessionId) {
            onSessionIdChange(sessionId);

            if (sessionIdChange && sessionIdChange !== sessionId) {
                console.log('*** LA SESION HA CAMBIADO:', sessionId);
                console.log('*** SE BORRA EL CHAT:');
                setControlAnswers([]);
                setAnswers([]);
            }
        }
    }, [sessionId, onSessionIdChange]);

    /************************************************************************ */


    const [errorMessage, setErrorMessage] = React.useState("");
    const [openAnswerDetails, setOpenAnswerDetails] = React.useState(false);
    const [selectedAB, setSelectedAB] = React.useState(0);
    const [selectedB, setSelectedB] = React.useState(0);

    const [anchorEl, setAnchorEl] = React.useState(null);
    const [historyLoading, setHistoryLoading] = React.useState(false);

    const scrollRef = useRef(null);
    const messagesRef = useRef(null);


    // Manejo del cambio de session para recuperación de historial ************************/
    useEffect(() => {
        // La recuperación de una respuesta es de la conversación que la
        // pidió: al abrir otra, empezar una nueva o borrarla, se cancela.
        if (recoveryRef.current && recoveryRef.current.sessionId !== sessionIdChange) {
            cancelRecovery();
        }

        // Solo si sessionIdChange es un ID válido y diferente al actual del chat
        if (sessionIdChange && sessionIdChange !== sessionId) {
            console.log('*** Chat.useEffect: Cargando sesión:', sessionIdChange);
            setSessionId(sessionIdChange);

            (async () => {
                setHistoryLoading(true);
                setAnswers([]);
                setControlAnswers([]);
                setHistoryBoundaryIndex(0);
                setNextHistoryCursor(null);
                try {
                    console.log("*** fetchData");
                    // Paginado real (2026-09-24): solo trae la última página
                    // de turnos (más nuevos primero), no la conversación
                    // completa -- ver getConversation en ConversationApi.js.
                    const conversationResult = await getConversation(sessionIdChange, { limit: HISTORY_PAGE_SIZE });
                    const savedConversation = conversationResult.success ? conversationResult.data : null;
                    console.log('***sessionIdChange:', sessionIdChange)
                    console.log('***sessionIdChange:', savedConversation)
                    console.log('***Chat.useEffect: Cargando conversación guardada:', savedConversation);

                    if (savedConversation && savedConversation.session_id) { // Verifica si savedConversation existe Y tiene un session_id
                        console.log('Cargando conversación guardada:', savedConversation);
                        // El backend devuelve los turnos más NUEVOS primero
                        // (ScanIndexForward=False, más barato de paginar) --
                        // se invierten acá para el orden cronológico que
                        // espera el resto del componente (el más viejo de
                        // esta página arriba, el más nuevo abajo).
                        const chronological = [...(savedConversation.turns || [])].reverse();
                        const loadedAnswers = chronological.map((t) => ({
                            ...t.turn,
                            _key: `${t.source}-${t.turn_index}`,
                            _serverTurnIndex: t.turn_index,
                        }));
                        const loadedControls = chronological.map((t) => t.control || {});
                        setAnswers(loadedAnswers);
                        setControlAnswers(loadedControls);
                        // Todo lo cargado acá es historial ya guardado -- se
                        // muestra completo (sin efecto de escritura), y es
                        // el punto de partida a partir del cual un turno
                        // nuevo de esta sesión necesita crearse (POST) en
                        // vez de asumirse ya persistido.
                        setHistoryBoundaryIndex(loadedAnswers.length);
                        setNextHistoryCursor(savedConversation.next_cursor || null);
                        const unanswered = unansweredTurnToRecover(loadedAnswers);
                        if (unanswered) {
                            recoverUnansweredTurn(sessionIdChange, unanswered);
                        }
                    } else {
                        // Esto incluye los casos donde savedConversation es undefined/null,
                        // o donde savedConversation existe pero no tiene un session_id (lo cual sería un dato incompleto)
                        console.log('***Chat.useEffect: No hay conversación válida o está incompleta, se reinicia el chat:');
                        setControlAnswers([]);
                        setAnswers([]);
                    }


                } catch (error) {
                    console.error('Error al cargar conversación:', error);
                } finally {
                    setHistoryLoading(false);
                }
            })();

        } else if (!sessionIdChange) {
            if (sessionId !== null) {
                console.log('*** Chat.useEffect: Session reset triggered from Layout');
                setSessionId(null);
            }
            setControlAnswers([]);
            setAnswers([]);
            setHistoryBoundaryIndex(0);
            setNextHistoryCursor(null);
            if (hasInteractedWithInput) {
                setHasInteractedWithInput(false);
            }
            if (onSessionIdChange) {
                onSessionIdChange(null);
            }
            setHistoryLoading(false);
        }
    // recoverUnansweredTurn fuera a propósito: es una función nueva en cada
    // render y solo se usa al terminar de cargar el historial.
    }, [sessionIdChange, sessionId, onSessionIdChange, hasInteractedWithInput]); // eslint-disable-line react-hooks/exhaustive-deps


    // Persistir turnos NUEVOS (control de costos/rendimiento, 2026-09-24):
    // reemplaza al guardado viejo (reenviar el array `answers` completo en
    // cada cambio) por un POST turno-a-turno -- solo los índices desde
    // `historyBoundaryIndex` en adelante son de esta sesión de navegación;
    // todo lo anterior ya está persistido (viene del backend). Cada turno
    // se crea una única vez (`_serverTurnIndex`/`_appendPromise` lo
    // marcan); las actualizaciones posteriores a un turno ya creado
    // (gráfica, feedback) las maneja `persistTurnChange` por separado.
    useEffect(() => {
        if (!sessionId || sessionIdChange !== sessionId) {
            return;
        }
        for (let i = historyBoundaryIndex; i < answers.length; i++) {
            const turnObj = answers[i];
            if (!turnObj || turnObj._serverTurnIndex !== undefined || turnObj._appendPromise) {
                continue;
            }
            if (!turnObj._key) {
                turnObj._key = `local-${uuidv4()}`;
            }
            const controlObj = controlAnswers[i] || {};
            const appendPromise = appendConversationTurn(
                sessionId,
                stripInternalTurnFields(turnObj),
                controlObj
            ).then((result) => {
                if (result.success) {
                    turnObj._serverTurnIndex = result.data.turn_index;
                    return result.data.turn_index;
                }
                console.error('Error al guardar el turno de la conversación:', result.error);
                return null;
            }).catch((error) => {
                console.error('Error al guardar el turno de la conversación:', error);
                return null;
            });
            turnObj._appendPromise = appendPromise;
        }
    }, [sessionId, sessionIdChange, answers, controlAnswers, historyBoundaryIndex]);


    //***********************************************************************************/





    useEffect(() => {
        // Desplaza solo la lista de mensajes: scrollIntoView también movía
        // la página y cualquier otro ancestro con scroll.
        const messages = messagesRef.current;
        if (messages) {
            messages.scrollTo({ top: messages.scrollHeight, behavior: "smooth" });
        }
        // `loading` además de `answers`: el indicador de "Respondiendo a tu
        // pregunta..." se agrega/quita después de `scrollRef` en el DOM sin
        // que `answers` necesariamente cambie en el mismo render -- sin
        // este dependency, el usuario podía quedar scrolleado por encima
        // del spinner en algunos casos.
    }, [answers, loading]);


    /*********************************************************************
    * Actualiza el sessionId en el componente padre
    **********************************************************************/
    useEffect(() => {
        if (onRequestNewSessionId) {
            onRequestNewSessionId(generateNewSessionId); // Pasa la función para generar el nuevo ID
            console.log('***Chat.useEffect.onRequestNewSessionId: No hay conversación, se reinicia el chat:');
            //setControlAnswers([]);
            //setAnswers([]);
        }
    }, [onRequestNewSessionId]);

    /*********************************************************************
    * Workspace change effect
    **********************************************************************/
    useEffect(() => {
        if (selectedWorkspace) {
            setWorkspaceError(null);
            if (answers.length > 0) {
                console.warn(
                    `⚠️ Workspace cambió a "${selectedWorkspace.name}" durante una conversación activa. ` +
                    `Las siguientes consultas usarán el agente del nuevo workspace.`
                );
            }
        }
    }, [selectedWorkspace, answers.length]);

    /*********************************************************************
    * Workspace change callback handler
    **********************************************************************/
    const handleWorkspaceChange = useCallback((workspace) => {
        console.log('Workspace changed in Chat:', workspace);
    }, []);


    /*********************************************************************
     * Efecto que se ejecuta solo una vez al montar el componente
     * Loading
     *********************************************************************/
    const effectRan = React.useRef(false);
    useEffect(() => {
        if (!effectRan.current) {
            console.log("effect applied - only on the FIRST mount");
            const fetchData = async () => {
                console.log("Chat")
            }
            fetchData()
                // catch any error
                .catch(console.error);
        }
        return () => effectRan.current = true;
    }, [setAnswers]);


    const handleExample = (value) => () => {
        getAnswer(value)
        setAnchorEl(null)
    };

    const handleMenuQuestions = (event) => {
        if (!loading) {
            setAnchorEl(event.currentTarget)
        }
    };

    const handleCloseMenuQuestions = () => {
        setAnchorEl(null)
    };

    // 1. MEMOIZAR el manejo del query para evitar re-creaciones
    const handleQuery = useCallback((event) => {
        const newQuery = event.target.value.replace("\n", "");
        setQuery(newQuery);
        
        // Optimizar la validación
        const shouldEnable = newQuery.length > 0 && !loading;
        if (enabled !== shouldEnable) {
            setEnabled(shouldEnable);
        }
    }, [loading, enabled]);

    // Pregunta sugerida por un Análisis Profundo: se deja en la caja, lista
    // para enviar (no se envía sola: cada análisis consume créditos).
    const handleFollowUp = useCallback((question) => {
        setQuery(question);
        setEnabled(!loading);
    }, [loading]);

    // 2. MEMOIZAR el handleKeyPress
    const handleKeyPress = useCallback((event) => {
        if (event.code === "Enter" && !loading && query !== "") {
            getAnswer(query);
            console.log("---> CONTROL DE RESPUESTAS: ", controlAnswers.length);
            
            if (controlAnswers.length === 0) {
                onSendQuery("true"); 
                onSendTextQuery(query);
            } else {
                onSendQuery("false");   
            }
        }
    // analysisMode / selectedModelId: getAnswer los lee; sin ellos, elegir
    // "Análisis Profundo" u otro modelo después de escribir la pregunta y
    // pulsar Enter enviaría el modo o el modelo previo.
    }, [loading, query, controlAnswers.length, onSendQuery, onSendTextQuery, analysisMode, selectedModelId]); // eslint-disable-line react-hooks/exhaustive-deps

    /*******************************************************
     * Send question to Agent
     ********************************************************/
    const handleClick = async (e) => {
        console.log("---> PREGUNTA ENVIADA");
        e.preventDefault();
        if (query !== "") {
            getAnswer(query);

            console.log("---> CONTROL DE RESPUESTAS: ", controlAnswers);
            // Send query to Layout.js
            if (controlAnswers.length === 1) {
                onSendQuery("true"); 
                onSendTextQuery(query);
            }else{
                onSendQuery("false");   
            }
        }
    }

    /***********************************************************************
     * Función que invoca al agente de Bedrock
     * @param {string} my_query - Pregunta del usuario
     ************************************************************************/
    const getAnswer = async (my_query) => {

        if (!loading && my_query !== "") {
            // Validate workspace selection before sending query
            if (!selectedWorkspace) {
                setWorkspaceError('Por favor selecciona un workspace');
                return;
            }

            // El agente lo resuelve agent-runtime a partir del workspace
            // (+ selectedAgentId si el usuario eligió uno en el sidebar).
            setWorkspaceError(null);
            
            const activeSessionId = ensureActiveSessionId();
            // Primera pregunta de una conversación nueva: la sesión recién se
            // crea, así que la elección del selector se guarda ahora.
            saveChatModel(activeSessionId, selectedModelId);
            const isDeep = analysisMode === 'deep';
            const isDashboard = analysisMode === 'dashboard';
            const mode = isDeep ? 'deep' : isDashboard ? 'dashboard' : 'basic';
            // `_key` desde el primer render: si se asignara después (en el
            // efecto que crea el turno), el siguiente re-render (p. ej. la
            // gráfica) cambiaría la key y React remontaría el turno.
            // askedAt/analysisMode/queryUuid (y pendingInvocationId al
            // aceptarse) se guardan con la pregunta para poder recuperar la
            // respuesta si la página se recarga antes de que llegue.
            const questionTurn = {
                query: my_query, _key: `local-${uuidv4()}`, askedAt: Date.now(), analysisMode: mode, queryUuid: uuidv4()
            };
            setControlAnswers(prevState => [...prevState, {}]);
            setAnswers(prevState => [...prevState, questionTurn]);
            setEnabled(false)
            setLoading(true)
            setProgressLabel("")
            setDeepProgress(isDeep ? initialDeepProgress() : null)
            setDashboardProgress(isDashboard ? initialDashboardProgress() : null)
            setErrorMessage("")
            setQuery("");

            try {
                const answer = await invokeBedrockAgent(my_query, activeSessionId, mode, {
                    queryUuid: questionTurn.queryUuid,
                    onAccepted: (invocationId) => {
                        questionTurn.pendingInvocationId = invocationId;
                        persistTurnChangeRef.current?.(questionTurn);
                    }
                });
                console.log("------- completion -------");
                console.log(answer.completion);
                const json = buildAnswerTurn(answer);
                setLoading(false);
                setProgressLabel("");
                setDeepProgress(null);
                setDashboardProgress(null);
                setEnabled(false);
                setControlAnswers(prevState => [...prevState, { current_tab_view: 'answer' }]);
                setAnswers(prevState => [...prevState, json]);

                // La gráfica se resuelve en segundo plano (no bloquea el render de
                // la respuesta): busca los resultados de consulta persistidos por
                // el executor del agente y, si hay datos, genera la configuración
                // de ApexCharts — todo del lado del servidor (whisperbi-agent-management).
                // Un tablero ya trae sus gráficas: no se pide otra a chart-data.
                if (!json.dashboard) {
                    fetchChartForAnswer(json);
                }
            } catch (error) {
                console.log('Call failed: ', error);
                // El servidor contestó con un error (sin créditos, plan, …):
                // la pregunta ya no está pendiente. Solo una conexión perdida
                // deja una respuesta que recuperar.
                if (error.code !== 'connection_lost') {
                    clearPendingInvocation(questionTurn);
                }
                if (error.code === 'connection_lost') {
                    // La pregunta sigue pendiente: la respuesta se recupera al
                    // volver a abrir la conversación (recoverUnansweredTurn).
                    setErrorMessage(
                        'Se perdió la conexión con el agente. La respuesta se recupera sola al volver '
                        + 'a abrir esta conversación cuando el análisis termine.'
                    );
                } else {
                    if (error.code === 'plan_required') {
                        setPlansModalRequest((value) => value + 1);
                    } else if (error.code === 'model_not_available') {
                        // Root desactivó el modelo elegido: se vuelve al del agente
                        // para que la próxima pregunta funcione.
                        handleModelChange(null);
                    }
                    // El error queda en la conversación, debajo de la pregunta.
                    appendQuestionError(error);
                }
                setLoading(false)
                setProgressLabel("")
                setDeepProgress(null)
                setDashboardProgress(null)
                setEnabled(false)
            }
        }
    }


    /***********************************************************************
     * Función que invoca al agente de Bedrock a través de Lambda
     * @param {string} prompt - Pregunta del usuario
     * @param {string} sessionId - ID de la sesión
     ************************************************************************/
    const invokeBedrockAgent = async (prompt, sessionId, mode = 'basic', { queryUuid = uuidv4(), onAccepted = null } = {}) => {
        try {
            // Validate workspace
            if (!selectedWorkspace) {
                throw new Error('No workspace selected');
            }

            // Get current date/time for session attributes
            const now = new Date();
            const formattedDate = now.toLocaleString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            });
            const formattedTime = now.toLocaleString('en-US', { timeStyle: 'short' });

            // Invoke agent via Lambda
            console.log('Invoking agent via Lambda...');
            const result = await invokeAgent({
                prompt,
                sessionId,
                workspaceId: selectedWorkspace.semantic_workspace_id,
                workspaceName: selectedWorkspace.name,
                agentConfigId: selectedAgentId,
                enableTrace: false,
                sessionAttributes: {
                    currentDate: formattedDate,
                    currentTime: formattedTime,
                    queryUuid: queryUuid
                },
                analysisMode: mode,
                modelId: selectedModelId,
                onAccepted,
                onProgress: (progress) => {
                    setProgressLabel(progress?.label || "");
                    if (mode === 'deep') {
                        setDeepProgress((state) => reduceDeepProgress(state, progress));
                    }
                    if (mode === 'dashboard') {
                        setDashboardProgress((state) => reduceDashboardProgress(state, progress));
                    }
                }
            });

            if (!result.success) {
                const invokeError = new Error(result.error || 'Error invoking agent');
                invokeError.code = result.errorCode;
                invokeError.reason = result.errorReason;
                invokeError.retryAfterSeconds = result.retryAfterSeconds;
                throw invokeError;
            }

            console.log('Agent invocation successful:', {
                invocation_id: result.data.invocation_id,
                tokens: result.data.usage?.total_tokens
            });
            return answerFromAgentData(result.data, queryUuid);

        } catch (error) {
            console.error('Error in invokeBedrockAgent:', error);
            throw error;
        }
    };

    const handleFormatter = (obj) => {
        if (typeof obj === 'object' && obj !== null) {
            for (let key in obj) {
                if (typeof obj[key] === 'string') {
                    if (key === "formatter" && (obj[key] === "%" || obj[key].startsWith("$"))) {
                        handleFormatter(obj[key]);
                        // Convert the function string to an actual function
                    } else if (key === "formatter") {
                        obj[key] = new Function('return ' + obj[key])();
                    } else {
                        handleFormatter(obj[key]);
                    }
                } else if (typeof obj[key] === 'object') {
                    handleFormatter(obj[key]);
                }
            }
        }
        return obj;
    };

    /***********************************************************************
     * Persiste una actualización tardía de un turno YA creado (gráfica que
     * llega en segundo plano, feedback de pulgar arriba/abajo) -- sin esto
     * esos datos solo viven en el estado de React y se pierden la próxima
     * vez que se abra el historial. Espera `_appendPromise` si el POST de
     * creación todavía no había resuelto cuando llegó esta actualización.
     * Best-effort: un fallo acá nunca debe romper la UI (mismo criterio que
     * el resto de las llamadas de red de este archivo).
     ************************************************************************/
    const persistTurnChange = useCallback(async (turnObj) => {
        if (!sessionId || !turnObj) {
            return;
        }
        try {
            let turnIndex = turnObj._serverTurnIndex;
            if (turnIndex === undefined) {
                if (!turnObj._appendPromise) {
                    return; // Todavía no se disparó ni siquiera la creación.
                }
                turnIndex = await turnObj._appendPromise;
                if (turnIndex === null || turnIndex === undefined) {
                    return; // La creación falló -- no hay a qué turno actualizar.
                }
            }
            const posInArray = answersRef.current.indexOf(turnObj);
            const control = posInArray >= 0 ? (controlAnswersRef.current[posInArray] || {}) : {};
            const result = await updateConversationTurn(sessionId, turnIndex, stripInternalTurnFields(turnObj), control);
            if (!result.success) {
                console.error('Error al persistir la actualización del turno:', result.error);
            }
        } catch (error) {
            console.error('Error al persistir la actualización del turno:', error);
        }
    }, [sessionId]);

    useEffect(() => { persistTurnChangeRef.current = persistTurnChange; }, [persistTurnChange]);
    useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

    // Error de una pregunta -> turno en la conversación (se guarda con ella).
    const appendQuestionError = (error) => {
        setControlAnswers(prevState => [...prevState, { current_tab_view: 'answer' }]);
        setAnswers(prevState => [...prevState, questionErrorTurn(error, `local-${uuidv4()}`)]);
    };

    // Botón del mensaje de error: abrir planes o ir a conectar datos.
    const handleQuestionErrorAction = useCallback((action) => {
        if (action === 'plans') {
            setPlansModalRequest((value) => value + 1);
        } else if (action === 'connections') {
            navigate('/admin/home/semantic-models/conexiones');
        }
    }, [navigate]);

    const clearPendingInvocation = (questionTurn) => {
        if (!questionTurn?.pendingInvocationId) return;
        delete questionTurn.pendingInvocationId;
        persistTurnChangeRef.current?.(questionTurn);
    };

    /***********************************************************************
     * Respuesta de una pregunta que quedó sin contestar en el historial: el
     * turno siguió en el servidor (que guarda la respuesta si no la pudo
     * entregar) mientras la página se recargaba o el teléfono la descartaba.
     ************************************************************************/
    const cancelRecovery = () => {
        if (!recoveryRef.current) return;
        recoveryRef.current.controller.abort();
        recoveryRef.current = null;
        setLoading(false);
        setProgressLabel("");
    };

    useEffect(() => () => recoveryRef.current?.controller.abort(), []);

    const recoverUnansweredTurn = async (recoverSessionId, questionTurn) => {
        recoveryRef.current?.controller.abort();
        const recovery = { sessionId: recoverSessionId, controller: new AbortController() };
        recoveryRef.current = recovery;
        setLoading(true);
        setEnabled(false);
        setErrorMessage("");
        setProgressLabel('Recuperando la respuesta…');
        const result = await recoverPendingTurn(questionTurn.pendingInvocationId, {
            askedAt: questionTurn.askedAt,
            analysisMode: questionTurn.analysisMode,
            signal: recovery.controller.signal
        });
        if (recovery.controller.signal.aborted || sessionIdRef.current !== recoverSessionId) {
            return; // Cancelada: el usuario abrió otra conversación o la borró.
        }
        recoveryRef.current = null;
        setLoading(false);
        setProgressLabel("");
        // Ya no hay nada que esperar (llegó la respuesta, un error o venció
        // el plazo del turno): la pregunta deja de estar pendiente para no
        // volver a recuperarla cada vez que se abra la conversación.
        clearPendingInvocation(questionTurn);
        if (result?.success) {
            const json = buildAnswerTurn(answerFromAgentData(result.data, questionTurn.queryUuid));
            setControlAnswers(prevState => [...prevState, { current_tab_view: 'answer' }]);
            setAnswers(prevState => [...prevState, json]);
            if (!json.dashboard) {
                fetchChartForAnswer(json);
            }
        } else if (result) {
            appendQuestionError({ code: result.errorCode, reason: result.errorReason,
                                  message: result.error, retryAfterSeconds: result.retryAfterSeconds });
        } else {
            appendQuestionError({ code: 'recovery_failed' });
        }
    };

    // "Guardar tablero" (modo Dashboard): lo guarda en Mis tableros con el
    // workspace actual y la pregunta que lo generó; el id queda en el turno
    // (y se persiste) para mostrar "Guardado" también al reabrir el historial.
    const handleSaveDashboard = useCallback(async (answerIndex) => {
        const turn = answersRef.current[answerIndex];
        if (!turn?.dashboard || turn.savedDashboardId || turn._dashboardSaving) return;
        const refresh = () => setAnswers((prev) => [...prev]);
        turn._dashboardSaving = true;
        turn._dashboardSaveError = null;
        refresh();
        try {
            const saved = await DashboardsService.save({
                workspaceId: selectedWorkspace?.semantic_workspace_id,
                dashboard: turn.dashboard,
                sourceQuestion: answersRef.current[answerIndex - 1]?.query || ''
            });
            turn.savedDashboardId = saved.dashboard_id;
            persistTurnChange(turn);
        } catch (error) {
            turn._dashboardSaveError = error.message || 'No se pudo guardar el tablero.';
        } finally {
            turn._dashboardSaving = false;
            refresh();
        }
    }, [selectedWorkspace, persistTurnChange]);

    const handleOpenDashboard = useCallback((dashboardId) => {
        navigate(`/layautapp/tableros/${dashboardId}`);
    }, [navigate]);


    /***********************************************************************
     * Busca (en segundo plano) los resultados de consulta asociados a una
     * respuesta y, si existen, la configuración de gráfica ya generada por
     * el backend (whisperbi-agent-management: /agent/chart-data). No
     * bloquea el render de la respuesta — actualiza el mismo objeto `json`
     * ya presente en `answers` y fuerza un re-render cuando resuelve.
     ************************************************************************/
    const fetchChartForAnswer = async (json) => {
        try {
            // Turno por WebSocket: el servidor ya está generando la gráfica
            // (sin el corte de 29 s de HTTP) y la manda por la conexión.
            const { queryResults, chart, credits_consumed: chartCredits } = json.chartPending
                ? await awaitTurnChart(json.invocationId)
                : await ChartDataAPI.getChartData({
                    queryUuid: json.queryUuid,
                    answerText: json.text,
                    invocationId: json.invocationId
                });
            json.chartLoading = false;
            if (typeof chartCredits === 'number') {
                json.chartCredits = chartCredits;
            }

            if (!queryResults || queryResults.length === 0) {
                // Quitar el "Generando gráfica…" aunque no haya nada que graficar.
                setAnswers(prevState => [...prevState]);
                return;
            }

            json.queryResults = queryResults;

            if (chart && chart.chart_configuration) {
                chart.chart_configuration = handleFormatter(chart.chart_configuration);
                chart.chart_configuration.options.chart.zoom = { enabled: false };

                // Fix the tooltip configuration issue
                if (chart.chart_configuration.options.tooltip) {
                    if (chart.chart_configuration.options.tooltip.shared === true) {
                        chart.chart_configuration.options.tooltip.intersect = false;
                    } else if (chart.chart_configuration.options.tooltip.intersect === true) {
                        chart.chart_configuration.options.tooltip.shared = false;
                    }
                }

                if (chart.chart_configuration.options.hasOwnProperty("title")) {
                    chart.chart_configuration.options.title.align = "center";
                }
                if (chart.chart_configuration.options.hasOwnProperty("subtitle")) {
                    chart.chart_configuration.options.subtitle.align = "center";
                }
            }

            json.chart = chart;
            // `json` ya está dentro de `answers`; forzar una nueva referencia
            // de array para que React vuelva a renderizar con el chart resuelto.
            setAnswers(prevState => [...prevState]);
            // Persistir la gráfica en el turno ya guardado -- si no se hace
            // acá, se pierde al recargar el historial (la gráfica llega
            // DESPUÉS de que el turno ya se creó). Por la ref: esta función
            // puede venir de un render anterior a fijar la sesión (respuesta
            // recuperada al abrir el historial, primera pregunta de un chat
            // nuevo) y su persistTurnChange tendría sessionId vacío.
            persistTurnChangeRef.current?.(json);
        } catch (error) {
            console.error('Error al obtener la gráfica:', error);
            // Antes el error se descartaba y la gráfica simplemente no
            // aparecía, sin ninguna pista (así pasó inadvertido el 402 de las
            // cuentas Free, ver chart-data CHART_OPERATION_TYPE).
            json.chartLoading = false;
            json.chartError = chartErrorMessage(error);
            setAnswers(prevState => [...prevState]);
        }
    };

    const handleCloseAnswerDetails = () => {
        setOpenAnswerDetails(false);
    };

    const handleClickOpenAnswerDetails = (value) => () => {
        setSelectedAB(value);
        setSelectedB(value);
        setOpenAnswerDetails(true);
    };

    const handleShowTab = (index, type) => () => {
        const updatedItems = [...controlAnswers];
        updatedItems[index] = { ...updatedItems[index], current_tab_view: type };
        setControlAnswers(updatedItems);
    };

    const handleResponseFeedback = useCallback((answerIndex, isOk) => {
        let updatedTurn = null;
        setAnswers(prevAnswers => {
            if (!Array.isArray(prevAnswers) || !prevAnswers[answerIndex]) {
                return prevAnswers;
            }
            const current = prevAnswers[answerIndex];
            const wasAlreadyPositive = current.is_response_ok === true;
            const updatedAnswers = [...prevAnswers];
            updatedTurn = { ...current, is_response_ok: isOk };
            updatedAnswers[answerIndex] = updatedTurn;

            // Fase 8 (VQR, curación semi-automática): un "👍" es la señal
            // explícita de que esta respuesta es confiable -- se envía como
            // candidato a verified query (queda PENDING_REVIEW hasta que un
            // admin lo apruebe en "Agentes → Consultas verificadas"). Nunca
            // bloquea el render ni molesta al usuario si falla (p. ej. la
            // respuesta salió solo de la Knowledge Base, sin SQL asociado
            // -- ver verified_queries.py::create_candidate_from_feedback) ni
            // se reenvía si ya se había marcado positiva antes.
            if (isOk && !wasAlreadyPositive && current.queryUuid) {
                const userQuestion = prevAnswers[answerIndex - 1]?.query;
                if (userQuestion && selectedAgentId) {
                    VerifiedQueryService.submitCandidate({
                        queryUuid: current.queryUuid,
                        question: userQuestion,
                        agentConfigId: selectedAgentId
                    }).catch((error) => {
                        console.log('VQR: no se pudo capturar el candidato (probablemente sin SQL asociado):', error?.message);
                    });
                }
            }

            return updatedAnswers;
        });
        // Fuera del updater (debe ser puro): persiste el feedback en el
        // turno ya guardado -- `updatedTurn` ya tiene la referencia final
        // gracias al closure de arriba, `setAnswers` la ejecuta sincrónico.
        if (updatedTurn) {
            persistTurnChange(updatedTurn);
        }
    }, [setAnswers, selectedAgentId, persistTurnChange]);

    // Trae la página SIGUIENTE (más antigua) del historial y la antepone al
    // principio de `answers`/`controlAnswers` -- todo lo que ya estaba
    // cargado se desplaza `olderAnswers.length` posiciones, así que
    // `historyBoundaryIndex` sube en la misma cantidad para seguir
    // apuntando al mismo límite real entre "historial" y "turnos nuevos de
    // esta sesión" (los objetos de turno viven en las mismas referencias,
    // así que `_serverTurnIndex`/`_appendPromise` no se pierden con el
    // desplazamiento).
    const handleLoadMoreHistory = useCallback(async () => {
        if (!sessionId || !nextHistoryCursor || loadingMoreHistory) {
            return;
        }
        setLoadingMoreHistory(true);
        try {
            const result = await getConversation(sessionId, { limit: HISTORY_PAGE_SIZE, cursor: nextHistoryCursor });
            if (result.success && result.data) {
                const olderChronological = [...(result.data.turns || [])].reverse();
                const olderAnswers = olderChronological.map((t) => ({
                    ...t.turn,
                    _key: `${t.source}-${t.turn_index}`,
                    _serverTurnIndex: t.turn_index,
                }));
                const olderControls = olderChronological.map((t) => t.control || {});
                setAnswers(prev => [...olderAnswers, ...prev]);
                setControlAnswers(prev => [...olderControls, ...prev]);
                setHistoryBoundaryIndex(prev => prev + olderAnswers.length);
                setNextHistoryCursor(result.data.next_cursor || null);
            } else if (!result.success) {
                console.error('Error al cargar más historial:', result.error);
            }
        } catch (error) {
            console.error('Error al cargar más historial:', error);
        } finally {
            setLoadingMoreHistory(false);
        }
    }, [sessionId, nextHistoryCursor, loadingMoreHistory]);

    /********************************************************************
     *
     ******************************************************************** */
        return (
        <OptimizedChatComponent
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            answers={answers}
            controlAnswers={controlAnswers}
            handleShowTab={handleShowTab}
            loading={loading}
            progressLabel={progressLabel}
            deepProgress={deepProgress}
            dashboardProgress={dashboardProgress}
            analysisMode={analysisMode}
            onAnalysisModeChange={setAnalysisMode}
            selectedModelId={selectedModelId}
            onModelChange={handleModelChange}
            plansModalRequest={plansModalRequest}
            onFollowUp={handleFollowUp}
            onSaveDashboard={handleSaveDashboard}
            onOpenDashboard={handleOpenDashboard}
            onQuestionErrorAction={handleQuestionErrorAction}
            historyLoading={historyLoading}
            historyBoundaryIndex={historyBoundaryIndex}
            hasMoreHistory={!!nextHistoryCursor}
            loadingMoreHistory={loadingMoreHistory}
            onLoadMoreHistory={handleLoadMoreHistory}
            scrollRef={scrollRef}
            messagesRef={messagesRef}
            query={query}
            handleQuery={handleQuery}
            handleKeyPress={handleKeyPress}
            enabled={enabled}
            handleClick={handleClick}
            selectedAB={selectedAB}
            openAnswerDetails={openAnswerDetails}
            handleCloseAnswerDetails={handleCloseAnswerDetails}
            selectedB={selectedB}
            WELCOME_MESSAGE={WELCOME_MESSAGE}
            MAX_LENGTH_INPUT_SEARCH={MAX_LENGTH_INPUT_SEARCH}
            onFeedbackSelect={handleResponseFeedback}
            selectedWorkspace={selectedWorkspace}
            workspaceError={workspaceError}
            onWorkspaceChange={handleWorkspaceChange}
        />
    );

};

export default Chat;
